import { useContext } from 'react';
import { Responses } from "../../StateManagment/Responses";
import { useFirestoreGeneralOrderBy } from "../../firebase/useFirestore";
import MatrixMapGame from './elements/MatrixMapGame';
import PostCard from './elements/PostCard';
import ScaleGame from './elements/ScaleGame';
import { QuestionnaireContext } from '../../StateManagment/Questionnaire';
import { useTranslation } from '../../StateManagment/TranslationContext';
import ReadAloudButton from './ReadAloudButton';
import shouldShowMatrixRightColumn from '../../helpers/questionnaire/shouldShowMatrixRightColumn';
import matrixGridTemplate from '../../helpers/questionnaires/matrixGridTemplate'
import getMatrixCellColor from '../../helpers/questionnaire/matrixCellColor';
import matrixColumnLabel from '../../helpers/questionnaire/matrixColumnLabel';
import scaleRange from '../../helpers/questionnaire/scaleRange';
import VasSlider from './VasSlider';
import scaleStartLabel from '../../helpers/questionnaire/scaleStartLabel';
import {
  positionToScaleValue,
  matrixScaleColumns,
  positionToMatrixColumn,
  vasAnchors,
} from '../../helpers/questionnaire/vasScale';
/**
 * @typedef {Object} MatrixQuestionRow 
 * @property {string} ID
 * @property {string} Field
 * @property {string} Title
 * @property {number} Position
 * @property {string} Color
 * @property {string} Question
 */

/**
 * @typedef {Object} MatrixQuestionCol 
 * @property {string} ID
 * @property {string} Field
 * @property {string} Title
 * @property {string} Position
 * @property {string} Color
 * @property {string} Question
 */

const FieldType = ({
  field,
  languageCode,
  isVisible
}) => {
  // Context
  const { settings } = useContext(QuestionnaireContext);
  const { translations } = useTranslation();
  const [responses, setResponses] = useContext(Responses);

  // Firestore
  const multipleOptions = useFirestoreGeneralOrderBy('MultipleQuestionOptions', 'Field', field.ID, 'Position', 'asc');
  /** @type {MatrixQuestionRow[]} */
  const matrixRows = useFirestoreGeneralOrderBy('MatrixQuestionRows', 'Field', field.ID, 'Position', 'asc');
  /** @type {MatrixQuestionCol[]} */
  const matrixColumns = useFirestoreGeneralOrderBy('MatrixQuestionColumns', 'Field', field.ID, 'Position', 'asc');

  // ---------- helpers (all controlled) ----------
  const updateResponses = (fieldID, input, optionType, itemid, fieldType) => {
    setResponses(prev => {
      const singleTypes = new Set(['paragraph', 'scale', 'number', 'multiple-one', 'matrix-one']);
      const base = singleTypes.has(fieldType)
        ? prev.filter(r => !(r.FieldID === fieldID && r.FieldType === fieldType))
        : prev;

      return [
        ...base,
        {
          FieldID: fieldID,
          Input: input,
          OptionType: optionType,
          Itemid: itemid,
          FieldType: fieldType,
        },
      ];
    });
  };

  const handleDeselectMatrixOne = (
    isSelected,
    fieldID,
    columnTitle,
    rowID,
    rowTitle,
    fieldType,
    columnID,
    rowTitleRight
  ) => {
    setResponses(prev => {
      const withoutThisRow = prev.filter(
        r => !(r.FieldID === fieldID && r.FieldType === fieldType && r.RowID === rowID)
      );
      if (!isSelected) return withoutThisRow;

      return [
        ...withoutThisRow,
        {
          FieldID: fieldID,
          FieldType: fieldType,
          RowID: rowID,
          RowTitle: rowTitle,
          RowTitleRight: rowTitleRight || '',
          ColumnID: columnID,
          ColumnTitle: columnTitle,
          Input: columnTitle,
        }
      ];
    });
  };

  // keep selection presence for multi-multi; if "other", store empty string initially
  const handleDeselectMultiMulti = (isSelected, fieldID, input, optionType, _inputType, itemid, fieldType) => {
    setResponses(prev => {
      if (isSelected) {
        if (prev.some(r => r.FieldID === fieldID && r.Itemid === itemid && r.FieldType === fieldType)) return prev;
        const storedInput = optionType === 'other' ? '' : input;
        return [
          ...prev,
          { FieldID: fieldID, Input: storedInput, OptionType: optionType, Itemid: itemid, FieldType: fieldType }
        ];
      }
      // deselect removes both the selection record and any 'other' text record
      return prev.filter(r => !(r.FieldID === fieldID && r.Itemid === itemid && r.FieldType === fieldType));
    });
  };

  // this updates (or creates) the single 'other' record for a given item
  const updateOtherInput = (fieldID, input, itemid, fieldType) => {
    setResponses(prev => {
      const without = prev.filter(r => !(r.FieldID === fieldID && r.OptionType === 'other' && r.Itemid === itemid));
      return [
        ...without,
        { FieldID: fieldID, Input: input, OptionType: 'other', Itemid: itemid, FieldType: fieldType },
      ];
    });
  };

  // ---------- schuifbalk (VAS) ----------
  // Het antwoord ziet er precies zo uit als een klik op een rondje, plus de
  // exacte plek (`VasRaw`). AI-CONTEXT: See docs/features/vas-slider.md
  const untouchedText = translations?.["Nog niet beantwoord"] || "Nog niet beantwoord";

  /** @param {number} position - Plek op de balk, 0–100 */
  const setScaleFromSlider = (position) => {
    const value = positionToScaleValue(field, position);
    if (value === null) return;
    setResponses(prev => [
      ...prev.filter(r => !(r.FieldID === field.ID && r.FieldType === 'scale')),
      { FieldID: field.ID, FieldType: 'scale', Input: value, OptionType: 'normal', VasRaw: position },
    ]);
  };

  /**
   * @param {MatrixQuestionRow & {TitleRight?: string}} row
   * @param {MatrixQuestionCol|null} column - De gekozen kolom; null haalt het antwoord van de rij weg
   * @param {number|null} position - Plek op de balk; null bij een 'niet van toepassing'-vakje
   */
  const setMatrixRowAnswer = (row, column, position) => {
    setResponses(prev => {
      const withoutThisRow = prev.filter(
        r => !(r.FieldID === field.ID && r.FieldType === 'matrix-one' && r.RowID === row.ID)
      );
      if (!column) return withoutThisRow;

      // Zelfde tekst als de `value` van het rondje: de vertaalde kolomtitel.
      const title = String(translations?.[column.Title] || column.Title);
      const answer = {
        FieldID: field.ID,
        FieldType: 'matrix-one',
        RowID: row.ID,
        RowTitle: row.Title,
        RowTitleRight: row.TitleRight || '',
        ColumnID: column.ID,
        ColumnTitle: title,
        Input: title,
      };
      return [...withoutThisRow, position === null ? answer : { ...answer, VasRaw: position }];
    });
  };

  // ---------- handlers ----------
  const inputHandler = (e) => {
    const input = e.target.value;
    const fieldID = e.target.dataset.id;
    const fieldType = e.target.dataset.fieldtype;

    if (field.Type === 'paragraph') {
      updateResponses(fieldID, input, 'normal', undefined, field.Type);

    } else if (field.Type === 'scale') {
      const numberInput = Number.isFinite(parseInt(input, 10)) ? parseInt(input, 10) : null;
      updateResponses(fieldID, numberInput, 'normal', undefined, field.Type);

    } else if (field.Type === 'number') {
      // allow empty -> '' in UI, store number or ''
      const parsed = input === '' ? '' : (Number.isFinite(parseInt(input, 10)) ? parseInt(input, 10) : '');
      updateResponses(fieldID, parsed, 'normal', undefined, field.Type);

    } else if (field.Type === 'multiple-multiple') {
      const optionType = e.target.dataset.optiontype;
      const inputType = e.target.dataset.inputtype;
      const itemid = e.target.dataset.itemid;

      if (inputType === 'otherInput') {
        updateOtherInput(fieldID, input, itemid, field.Type);
      } else {
        const isSelected = e.target.checked;
        handleDeselectMultiMulti(isSelected, fieldID, input, optionType, inputType, itemid, field.Type);
      }

    } else if (field.Type === 'multiple-one') {
      const itemid = e.target.dataset.itemid;
      const optionType = e.target.dataset.optiontype; // 'other' | 'normal'
      // If 'other' radio is selected, store empty string for the selection record
      updateResponses(fieldID, optionType === 'other' ? '' : input, optionType, itemid, field.Type);

    } else if (field.Type === 'matrix-one') {
      const isSelected = e.target.checked;
      const rowID = e.target.dataset.rowid;
      const rowTitle = e.target.dataset.rowtitle;
      const rowTitleRight = e.target.dataset.rowtitleright;
      const columnID = e.target.dataset.columnid;
      const columnTitle = e.target.value;

      handleDeselectMatrixOne(
        isSelected,
        fieldID,
        columnTitle,
        rowID,
        rowTitle,
        field.Type,
        columnID,
        rowTitleRight
      );
    }
  };

  // ---------- render (all controlled) ----------

  // paragraph (controlled textarea)
  if (field.Type === 'paragraph') {
    const val = (responses.find(r => r.FieldID === field.ID && r.FieldType === 'paragraph')?.Input) ?? '';
    return (
      <div className="paragraph-container">
        <textarea
          cols="30"
          rows="10"
          placeholder={translations?.["Geef hier uw antwoord"] || "Geef hier uw antwoord"}
          data-id={field.ID}
          data-fieldtype={field.Type}
          value={val}
          onChange={inputHandler}
        />
        {/* Display-only char count; hidden outside the qo-redesign look (CSS) */}
        <div className="qo-charcount">{String(val).length}</div>
        {settings.miniGame === true && <PostCard isVisible={isVisible} />}
      </div>
    );
  }

  // scale als schuifbalk
  if (field.Type === 'scale' && settings.vas && scaleRange(field).length > 0) {
    const current = responses.find(r => r.FieldID === field.ID && r.FieldType === 'scale');
    const { left, right } = vasAnchors({ field, translations });

    return (
      <div className='vas-question'>
        <VasSlider
          value={current?.VasRaw ?? null}
          onChange={setScaleFromSlider}
          left={left}
          right={right}
          ariaLabel={translations?.[field.ID] || field.Question}
          untouchedText={untouchedText}
        />
      </div>
    );
  }

  // scale (controlled radios)
  if (field.Type === 'scale') {
    const range = scaleRange(field);
    const selected = responses.find(r => r.FieldID === field.ID && r.FieldType === 'scale')?.Input ?? null;

    return (
      <>
        <div id='scale-container'>
          <span className="scale-label">{translations?.[scaleStartLabel(field)] || scaleStartLabel(field)}</span>
          {range.map(btn => (
            <label key={field.ID + btn} id='question-type-label-container'>
              <input
                type="radio"
                name={field.ID}
                value={btn}
                data-id={field.ID}
                data-fieldtype={field.Type}
                checked={selected === btn}
                onChange={inputHandler}
              />
              <span>{btn}</span>
            </label>
          ))}
          <span className="scale-label">{translations?.[field.ReachEndLabel] || field.ReachEndLabel}</span>
        </div>
        {settings.miniGame && (
          <ScaleGame
            isVisible={isVisible}
            range={range}
            selected={selected || 0}
            overlay={false}
          />
        )}
      </>
    );
  }

  // number (controlled input[type=number])
  if (field.Type === 'number') {
    const selected = responses.find(r => r.FieldID === field.ID && r.FieldType === 'number')?.Input ?? '';
    return (
      <div id='scale-container'>
        <input
          type="number"
          id='question-number-input'
          value={selected}
          data-id={field.ID}
          data-fieldtype={field.Type}
          onChange={inputHandler}
        />
      </div>
    );
  }

  // multiple-multiple (controlled checkboxes + controlled "other" text)
  if (field.Type === 'multiple-multiple') {
    return (
      <div id='question-options-container'>
        {settings.miniGame && (
          <ScaleGame
            isVisible={isVisible}
            range={[0, 1, 2]}
            selected={1}
            overlay={true}
          />
        )}
        {multipleOptions && multipleOptions.map(item => {
          const isSelected = responses.some(r => r.FieldID === field.ID && r.Itemid === item.ID && r.FieldType === 'multiple-multiple');
          const otherVal = responses.find(r => r.FieldID === field.ID && r.Itemid === item.ID && r.OptionType === 'other')?.Input ?? '';

          const onChangeBox = (e) => {
            const val = e.target.value;
            const isChecked = e.target.checked;
            handleDeselectMultiMulti(
              isChecked,
              field.ID,
              val,
              item.Option === 'other' ? 'other' : 'normal',
              'checkboxInput',
              item.ID,
              field.Type
            );
          };

          const onChangeOtherText = (e) =>
            updateOtherInput(field.ID, e.target.value, item.ID, field.Type);

          return (
            <label key={'r' + item.ID}
                   htmlFor={item.ID}
                   className={`question-checkbox-label ${isSelected ? 'checked' : ''}`}>
              <input
                className="question-checkbox-input"
                type="checkbox"
                id={item.ID}
                name='options'
                data-itemid={item.ID}
                data-optiontype={item.Option === 'other' ? 'other' : 'normal'}
                data-inputtype='checkboxInput'
                data-id={field.ID}
                data-fieldtype={field.Type}
                value={translations?.[item.Option] || item.Option}
                checked={isSelected}
                onChange={onChangeBox}
              />
              <span>
                {item.Option === 'other'
                  ? (translations?.["Anders, namelijk..."] || "Anders, namelijk...")
                  : (translations?.[item.Option] || item.Option)}
              </span>

              {item.Option === 'other' && isSelected && (
                <input
                  id='multiple-question-other-input'
                  type="text"
                  data-id={field.ID}
                  data-itemid={item.ID}
                  data-fieldtype={field.Type}
                  data-optiontype='other'
                  data-inputtype='otherInput'
                  placeholder={translations?.["Schrijf hier je antwoord"] || "Schrijf hier je antwoord"}
                  value={otherVal}
                  onChange={onChangeOtherText}
                />
              )}
              <ReadAloudButton text={translations?.[item.Option] || item.Option} />
            </label>
          );
        })}
      </div>
    );
  }

  // multiple-one (controlled radios + controlled "other" text)
  if (field.Type === 'multiple-one') {
    return (
      <div id="question-options-container">
        {settings.miniGame && (
          <ScaleGame
            isVisible={isVisible}
            range={[0, 1, 2]}
            selected={1}
            overlay={true}
          />
        )}
        {multipleOptions && multipleOptions.map((item) => {
          const isSelected = responses.some(r => r.FieldID === field.ID && r.Itemid === item.ID && r.FieldType === 'multiple-one');
          const otherVal = responses.find(r => r.FieldID === field.ID && r.Itemid === item.ID && r.OptionType === 'other')?.Input ?? '';

          const onChangeOption = (e) => {
            const val = e.target.value;
            const optionType = e.target.dataset.optiontype;
            // for 'other', keep selection but start with empty input
            updateResponses(field.ID, optionType === 'other' ? '' : val, optionType, item.ID, field.Type);
          };

          const onChangeOtherText = (e) =>
            updateOtherInput(field.ID, e.target.value, item.ID, field.Type);

          return (
            <label key={item.ID}
                   htmlFor={item.ID}
                   className={`question-checkbox-label ${isSelected ? 'checked' : ''}`}>
              <input
                className="question-checkbox-input"
                type="radio"
                id={item.ID}
                name={`option-${field.ID}`}
                data-itemid={item.ID}
                data-optiontype={item.Option === 'other' ? 'other' : 'normal'}
                data-id={field.ID}
                value={translations?.[item.Option] || item.Option}
                data-type={item.Option}
                data-fieldtype={field.Type}
                checked={isSelected}
                onChange={onChangeOption}
              />
              <span>
                {item.Option === 'other'
                  ? (translations?.["Anders, namelijk..."] || "Anders, namelijk...")
                  : (translations?.[item.Option] || item.Option)}
              </span>

              {item.Option === 'other' && isSelected && (
                <input
                  id='multiple-question-other-input'
                  type="text"
                  data-id={field.ID}
                  data-itemid={item.ID}
                  data-fieldtype={field.Type}
                  data-optiontype="other"
                  data-inputtype='otherInput'
                  placeholder={translations?.["Schrijf hier je antwoord"] || "Schrijf hier je antwoord"}
                  value={otherVal}
                  onChange={onChangeOtherText}
                />
              )}
              <ReadAloudButton text={translations?.[item.Option] || item.Option} />
            </label>
          );
        })}
      </div>
    );
  }


  // matrix-one als schuifbalk: één balk per stelling
  if (field.Type === 'matrix-one' && settings.vas) {
    const bipolar = shouldShowMatrixRightColumn(field);
    const { scale, notApplicable } = matrixScaleColumns(field, matrixColumns);

    return (
      <div className='vas-matrix'>
        {matrixRows && matrixRows.map((row, rowIndex) => {
          const answer = responses.find(r =>
            r.FieldID === field.ID && r.FieldType === 'matrix-one' && r.RowID === row.ID
          );
          const { left, right } = vasAnchors({ field, row, columns: matrixColumns, translations });
          const rowTitle = translations?.[row.Title] || row.Title;

          return (
            <div key={row.ID} className='vas-matrix-row' data-testid={`vas-row-${row.ID}`}>
              {/* Bij een tweezijdige rij staan de stelling en haar tegenhanger
                  al aan de uiteinden van de balk. */}
              {!bipolar && (
                <p className='vas-matrix-statement'>
                  <span className='matrix-row-number' aria-hidden='true'>{rowIndex + 1}</span>
                  {rowTitle}
                </p>
              )}
              {/* Zonder schaalkolommen is er niets te kiezen; een balk die
                  beweegt maar niets opslaat zou liegen. */}
              {scale.length > 0 && (
                <VasSlider
                  value={answer?.VasRaw ?? null}
                  onChange={(position) => setMatrixRowAnswer(row, positionToMatrixColumn(scale, position), position)}
                  left={left}
                  right={right}
                  ariaLabel={bipolar ? `${left} – ${right}` : rowTitle}
                  untouchedText={untouchedText}
                />
              )}
              {notApplicable.map((column, index) => (
                <label key={column.ID} className='vas-not-applicable'>
                  <input
                    type='checkbox'
                    checked={answer?.ColumnID === column.ID}
                    onChange={(e) => setMatrixRowAnswer(row, e.target.checked ? column : null, null)}
                  />
                  <span>{matrixColumnLabel(column, scale.length + index, translations)}</span>
                </label>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  // matrix-one (controlled radios by row/column)
  if (field.Type === 'matrix-one') {
    const bipolar = shouldShowMatrixRightColumn(field);
    // `matrix-has-game`: het minispel tekent een laag OVER het raster en rekent
    // met de plek van elke cel. Onder de 768px stapelt de matrix zich tot losse
    // rijen (zie questionnaire.css), waardoor die berekening niet meer klopt —
    // die markering laat de gestapelde weergave het spel met rust laten.
    return (
      <div className={`matrix-grid-scroll-container matrix-grid-scroll-container-${field.ID}${settings.miniGame ? ' matrix-has-game' : ''}`}>
        <div
          id='matrix-grid-container'
          style={{
            // De stelling links is een zin en krijgt daarom meer ruimte dan een
            // bolletje; kregen ze allemaal 1fr, dan liep de tekst over de eerste
            // antwoordkolom heen.
            gridTemplateColumns: matrixGridTemplate(matrixColumns?.length, bipolar),
            // Containers narrower than this crop trailing columns behind an
            // invisible horizontal scroll (qo-redesign overrides via !important).
            // AI-CONTEXT: See docs/pitfalls.md#ui-inline-minwidth-hides-columns
            minWidth: '800px',
            position: 'relative'
          }}
        >
          {/* Column titles */}
          <div className='matrix-grid'>
            <div className='matrix-grid-header'></div>
            {matrixColumns && matrixColumns.map(column => (
              <div key={column.ID} className='matrix-grid-header'>
                <p>{translations?.[column.Title] || column.Title}</p>
                <ReadAloudButton text={translations?.[column.Title] || column.Title} />
              </div>
            ))}
            {bipolar && <div className='matrix-grid-header'></div>}
          </div>

          {/* Rows */}
          {matrixRows && matrixRows.map((row, rowIndex) => (
            <div key={row.ID} className='matrix-grid-row' style={{ display: 'contents' }}>
              <div className='matrix-grid-header first-column'>
                <p><span className='matrix-row-number' aria-hidden='true'>{rowIndex + 1}</span>{translations?.[row.Title] || row.Title}</p>
                <ReadAloudButton text={translations?.[row.Title] || row.Title} />
              </div>

              {matrixColumns && matrixColumns.map((column, colIndex) => {
                const isSelected = responses.some(r =>
                  r.FieldID === field.ID &&
                  r.FieldType === 'matrix-one' &&
                  r.RowID === row.ID &&
                  r.ColumnID === column.ID
                );
                const cellColor = getMatrixCellColor({ field, row, colIndex, totalCols: matrixColumns.length });

                return (
                  <label key={column.ID} className='matrix-grid-item'>
                    {/* De kolomnaam per cel. Op een breed scherm staat hij
                        alleen in de DOM (voor een schermlezer); onder de 768px
                        vervallen de kolomkoppen bovenaan en is dit het enige
                        wat de invuller nog vertelt waar dit bolletje voor
                        staat. */}
                    <span className='matrix-cell-label'>
                      {matrixColumnLabel(column, colIndex, translations)}
                    </span>
                    <div
                      className='matrix-radio-color-ring'
                      style={cellColor ? { '--matrix-cell-color': cellColor } : undefined}
                    >
                    <input
                      className='question-radio-input'
                      type="radio"
                      name={`row-${row.ID}`}
                      id={column.ID}
                      data-id={field.ID}
                      data-itemid={column.ID}
                      data-rowtitle={row.Title}
                      data-rowtitleright={row.TitleRight}
                      data-rowid={row.ID}
                      value={translations?.[column.Title] || column.Title}
                      data-columnid={column.ID}
                      data-fieldtype={field.Type}
                      checked={isSelected}
                      onChange={inputHandler}
                    />
                    </div>
                  </label>
                );
              })}

              {bipolar && (
                <div className='matrix-grid-header last-column'>
                  <p>{translations?.[row.TitleRight] || row.TitleRight}</p>
                  <ReadAloudButton text={translations?.[row.TitleRight] || row.TitleRight} />
                </div>
              )}
            </div>
          ))}

          {settings.miniGame && (
            <MatrixMapGame
              scrollContainerSelector={`.matrix-grid-scroll-container-${field.ID}`}
              isVisible={isVisible}
              rows={matrixRows}
              cols={matrixColumns}
              responses={responses.filter(r => r.FieldType === 'matrix-one')}
            />
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default FieldType;
